import React, { useMemo, useState } from "react";
import { customAttrDisplay } from "../../Core/modals/customAttributes/displayValue";
import { Modal, Button, message, DatePicker, Popover } from "antd";
import { UserOutlined, WarningOutlined, PlusOutlined, MinusOutlined, CalendarOutlined } from "@ant-design/icons";
import Offers from "../../../../assets/images/promoTag.svg";
import DeleteProduct from "../../../../assets/images/delete1.svg";
import NotesImage from "../../../../assets/images/notes.svg";
import NotesAdd from "../../../../assets/images/note-add.svg";
import userTieIcon from "../../../../assets/images/userTieIcon.svg";
import userAddIcon from "../../../../assets/images/userAddIcon.svg";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import dayjs from "dayjs";
import { trace } from "../../../../utils/debugTrace";

const SMCartTable = ({
  cart,
  cartItems,
  hasExpandableRows,
  posConfig,
  themeJSON,
  pricePrecision,
  currenciesList,
  tillData,
  selectedProductInCart,
  selectedRowKeys,
  isExchange,
  watermark,
  renderInvoice,
  renderDocumentNo,
  renderReturnAmount,
  renderExchangeAmount,
  selectProductInCart,
  setSelectedProductInCart,
  clearSelectedProductInCart,
  removeProductModal,
  setShowPaymentMethods,
  setNotesPopup,
  setNotesValue,
  setSelectedProductForNotes,
  setIsInputFocused,
  setShowInputFields,
  setCustomMixItems,
  setGroupQty,
  setActiveInput,
  searchInputRef,
  qtyInputRef,
  setSelectedCustomMix,
  setGroupName,
  setExistingGroupItems,
  setShowCustomMix,
  setOverPayedAmount,
  setAmount,
  setQtyNumberFlag,
  setRealPrice,
  setFirstInputValue,
  setStockList,
  setOpenSalesRep,
  setDisplayCustomerSearch,
  setCloseCustomerFlag,
  setProductSearchInput,
  handleDateSelect,
  setCart,
  removeCoupon,
  notesValue,
  activeCartTable,
  setActiveCartTable,
  exchangeNotesValue,
  setExchangeNotesValue,
}) => {
  const isReturnExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE" && cart?.items?.length > 0;
  // Discount column is omitted in return/exchange mode, so the remaining columns'
  // percentage widths must be rebalanced (they previously summed to only 85%). Actions
  // uses a fixed px width (same pattern as TSK-2132's BOM cart delete column) with
  // table-layout: fixed so it can never be squeezed off-screen by the discount badge
  // or longer totals (#1927).
  const colWidths = isReturnExchange
    ? { name: "33%", qty: "13%", tax: "13%", price: "12%", total: "17%", actions: "40px" }
    : { name: "28%", qty: "11%", discount: "11%", tax: "11%", price: "10%", total: "15%", actions: "10%" };
  const exchangeCart = useCartStore((s) => s.exchangeCart);
  const exchangeItems = useMemo(() => (exchangeCart?.items || []).filter((item) => item.weight !== 0), [exchangeCart?.items]);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroupExpand = (lineId) => {
    setExpandedGroups((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  const onRowClick = (record) => {
    if (cart?.layAway === "Y" && posConfig?.allowLayawaywithoutPayment === "Y") {
      message.info("This order cannot be edited!");
    } else {
      if (cart?.preOrder === "Y") {
        Modal.confirm({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Cart Editing Restricted</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>
                This is a pre-order with advance payment. Cart items cannot be modified at this stage. Please proceed to complete the transaction.
              </p>
            </div>
          ),
          icon: null,
          icon: <WarningOutlined />,
          okText: "Ok",
          cancelButtonProps: { style: { display: "none" } },
          autoFocusButton: null,
          modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
          onOk() {
            const productSearchInput = document.getElementById("sm-product-search");
            if (productSearchInput) {
              setTimeout(() => {
                productSearchInput.focus();
              }, 300);
            }
          },
        });
        return;
      }
      if (cart?.couponInput?.length > 0) {
        Modal.confirm({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Cart Editing Restricted</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0 }}>Coupon applied to this order. Do you want to remove the coupon and continue editing the cart?</p>
            </div>
          ),
          icon: <WarningOutlined />,
          okText: "Yes",
          cancelText: "No",
          autoFocusButton: null,
          okButtonProps: { style: { display: "inline-block" } },
          onOk: async () => {
            if (cart?.couponInput?.length > 0) {
              for (const coupon of cart.couponInput) {
                await removeCoupon(coupon);
              }
            }
          },

          onCancel() {
            const productSearchInput = document.getElementById("sm-product-search");
            if (productSearchInput) {
              setTimeout(() => {
                productSearchInput.focus();
              }, 300);
            }
          },
        });

        return;
      }
      qtyInputRef?.current?.focus();
      setActiveInput("qty");
      if (record.posPriceEdit === "Y") {
        qtyInputRef?.current?.focus();
        setShowInputFields(true);
        setRealPrice(record.weight);
        setFirstInputValue(true);
      } else {
        setShowInputFields(false);
      }
      if (record.children && record.children.length > 0) {
        // Children store SCALED weights (baseQty * groupQty). The CustomMix
        // modal expects per-unit weights and re-multiplies by groupQty on
        // submit, so passing scaled values straight through doubles the qty
        // (and price) on every reopen. Restore base via `baseQty`, falling
        // back to scaled/groupQty for legacy lines that pre-date that field.
        // (Tab8 issues #8/#9)
        const groupQtyVal = Number(record.weight) || 1;
        const baseChildren = record.children.map((c) => {
          const base = c.baseQty != null ? Number(c.baseQty) : groupQtyVal > 0 ? Number(c.weight) / groupQtyVal : Number(c.weight);
          return { ...c, weight: base, rawWeight: String(base) };
        });
        setSelectedCustomMix(record);
        setCustomMixItems(baseChildren);
        setGroupName(record.name);
        setGroupQty(record.weight);
        setExistingGroupItems(true);
        setShowCustomMix(true);
      }
      setIsInputFocused(false);
      setOverPayedAmount(0);
      // In exchange mode, use the combined net total (exchange + return) minus paid
      const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      if (isExchangeMode) {
        const exchCart = useCartStore.getState().exchangeCart || {};
        const netTotal = (Number(cart.total) || 0) + (Number(exchCart.total) || 0);
        const paidSoFar = Number(exchCart.paid) || 0;
        setAmount(parseFloat((netTotal - paidSoFar).toFixed(pricePrecision)));
      } else {
        setAmount(parseFloat(((Number(cart.total) || 0) - (Number(cart.paid) || 0)).toFixed(pricePrecision)));
      }
      if (isExchangeMode) {
        trace("SMSelection", "row clicked", {
          productId: record.productId,
          name: record.name,
          weight: record.weight,
          isReturn: record.isReturn,
          lineId: record.lineId,
        });
      }
      selectProductInCart(record);
      setQtyNumberFlag(0);
      setShowPaymentMethods(false);
      setSelectedProductInCart(record);
      setStockList([]);
    }
  };

  const renderItemName = (record) => (
    <>
      <div className="offerName" style={{ display: "flex", alignItems: "center" }}>
        {posConfig?.showOrderSalesRep === "Y" && posConfig?.showLineSalesRep === "Y" ? (
          <>
            {record.salesRepName ? (
              <>
                <img src={userTieIcon} style={{ height: "2vh", marginRight: "5px", marginBottom: "2px" }} />
                <span>{record.salesRepName}</span>
              </>
            ) : (
              cart.salesRepName && (
                <>
                  <img src={userTieIcon} style={{ height: "2vh", marginRight: "5px", marginBottom: "2px" }} />
                  <span>{cart.salesRepName}</span>
                </>
              )
            )}
          </>
        ) : null}
      </div>
      <div
        onClick={() => {
          if (cart.type !== "Layaway") {
            selectProductInCart(record);
            selectedRowKeys.length > 0 && selectedProductInCart.productId === record.productId ? setShowPaymentMethods(false) : "";
          }
        }}
        id="sm-product-name"
        className="tableRow"
        style={{ textAlign: "left", marginLeft: 0, display: "flex", alignItems: "center" }}
      >
        {record.children && record.children.length > 0 && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleGroupExpand(record.lineId);
            }}
            style={{
              cursor: "pointer",
              marginRight: "6px",
              fontSize: "12px",
              width: "18px",
              height: "18px",
              borderRadius: "3px",
              border: "1px solid #ccc",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: expandedGroups[record.lineId] ? "#2F3856" : "#f0f0f0",
              color: expandedGroups[record.lineId] ? "#fff" : "#333",
              flexShrink: 0,
            }}
          >
            {expandedGroups[record.lineId] ? "-" : "+"}
          </span>
        )}
        {record.name}
        {record.children && record.children.length > 0 && <span style={{ fontSize: "0.7em", color: "#888", marginLeft: "6px" }}>({record.children.length} items)</span>}
      </div>
      <div className="offerName" style={{}}>
        <p style={{ margin: 0 }}>{record.cardNo ? record.cardNo : record.value}</p>
        {record.newCustomAttributes &&
          record.newCustomAttributes.length > 0 &&
          Object.entries(record.newCustomAttributes[0]).map(([key, value]) => {
            const display = customAttrDisplay(value);
            if (!display) return null;
            return (
              <p key={key} style={{ margin: 0, padding: 0, color: "#8c92ac", fontStyle: "italic" }}>
                {key} - {display}
              </p>
            );
          })}
        {record.selectedAddons &&
          record.selectedAddons.length > 0 &&
          record.selectedAddons.map(
            (item, idx) =>
              item?.name && (
                <p key={idx} style={{ margin: 0, padding: 0, color: "#8c92ac", fontStyle: "italic" }}>
                  {item.name}
                </p>
              )
          )}
        {record.batchedProduct === "Y" && record.batchedForSale === "Y" && record.batchno && (
          <p style={{ margin: 0, padding: 0, color: "#8c92ac", fontStyle: "italic" }}>Batch: {record.batchno}</p>
        )}
        {(() => {
          const uomName = record?.uomData?.[0]?.name || record?.selectedUom?.name || record?.uom_name || "";
          return uomName ? <p style={{ margin: 0, padding: 0, color: "#8c92ac", fontStyle: "italic", fontSize: "0.75em" }}>UOM: {uomName}</p> : null;
        })()}
        {Math.abs(record.discount) > 0 && record.discountName ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              marginTop: "2px",
              padding: "2px 8px",
              backgroundColor: "#FFF7E0",
              border: "1px solid #F8C519",
              color: "#7A5A00",
              borderRadius: "10px",
              fontSize: "0.72em",
              lineHeight: 1.2,
              fontWeight: 500,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <img src={Offers} alt="" width={12} height={12} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{record.discountName}</span>
          </span>
        ) : null}
      </div>
    </>
  );

  const renderQty = (record) => {
    const wt = parseFloat(record.weight);
    if (Number.isInteger(wt)) {
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="tableRow">{wt.toFixed(record.isQtyDesimal ?? 2)}</div>
        </div>
      );
    }
    return (
      <>
        <div>{parseFloat(record.weight).toFixed(record.isQtyDesimal ?? 2)}</div>
        <div className="offerName">{record.salesRepName && posConfig?.showOrderSalesRep === "Y" && posConfig?.showLineSalesRep === "Y" ? record.salesRepName : ""}</div>
      </>
    );
  };

  const renderActions = (record, cartType = "RETURN") => {
    if (record.groupedItems === true) return null;
    return (
      <div style={{ position: "relative", display: "flex", justifyContent: "space-evenly" }}>
        <img
          src={DeleteProduct}
          onClick={(e) => {
            e.stopPropagation();
            clearSelectedProductInCart();
            removeProductModal(record);
            setIsInputFocused(false);
            setShowInputFields(false);
            // Clear all custom mix state so stale items don't persist in popup (#11)
            setCustomMixItems([]);
            setGroupQty(1);
            setGroupName("Custom Mix");
            setSelectedCustomMix({});
            setExistingGroupItems(false);
            setActiveInput("search");
            if (searchInputRef?.current) searchInputRef.current.focus();
          }}
          id="sm-product-delete"
          style={{ height: "2.5vh", cursor: "pointer", top: "15%" }}
        />
        {posConfig?.enableLineLevelNarration === "Y" && (
          <img
            onClick={(e) => {
              e.stopPropagation();
              setActiveCartTable(cartType);
              clearSelectedProductInCart();
              setNotesPopup(true);
              setIsInputFocused(true);
              if (cartType === "EXCHANGE") {
                setExchangeNotesValue(record.notes || "");
              } else {
                setNotesValue(record.notes || "");
              }
              setSelectedProductForNotes(record);
              selectProductInCart(record);
              setIsInputFocused(false);
            }}
            src={NotesImage}
            alt="Notes"
            title="Add Note"
            style={{ height: "2.5vh", cursor: "pointer", opacity: 0.7 }}
          />
        )}
        {!isReturnExchange && (
          <>
            {posConfig?.showLineSalesRep === "Y" && (
              <img src={userAddIcon} style={{ height: "2.5vh", cursor: "pointer", paddingTop: "1px" }} alt="" onClick={() => setOpenSalesRep({ flag: true, level: "line" })} />
            )}
          </>
        )}
      </div>
    );
  };

  const renderChildRow = (child) => {
    const currSymbol = currenciesList[0]?.currSymbol || "";
    const symbolRight = currenciesList[0]?.symbolRightSide === "Y";
    const symbolLeft = currenciesList[0]?.symbolRightSide === "N";
    const childTax = child.taxAmount?.toFixed(pricePrecision).length < 8 ? parseFloat(child.taxAmount)?.toFixed(pricePrecision) : `${child.taxAmount?.toString().substring(0, 8)}`;
    const uomName = child?.uomData?.[0]?.name || child?.selectedUom?.name || "";

    return (
      <tr key={child.lineId} style={{ backgroundColor: "#f8f9fa", borderLeft: "3px solid #2F3856" }}>
        <td style={{ width: colWidths.name, padding: "4px 8px 4px 28px", verticalAlign: "top" }}>
          <div style={{ fontSize: "0.75rem", color: "#555" }}>
            {child.name}
            {uomName && <span style={{ color: "#999", marginLeft: "4px", fontSize: "0.65rem" }}>({uomName})</span>}
          </div>
        </td>
        <td style={{ width: colWidths.qty, padding: "4px 8px", textAlign: "center", verticalAlign: "middle" }}>
          <span style={{ fontSize: "0.75rem", color: "#555" }}>{parseFloat(child.weight).toFixed(child.isQtyDesimal ?? 2)}</span>
        </td>
        {!isReturnExchange && (
          <td style={{ width: colWidths.discount, padding: "4px 8px", textAlign: "center", verticalAlign: "middle" }}>
            <span style={{ fontSize: "0.75rem", color: "#555" }}>{parseFloat(child.discount || 0).toFixed(pricePrecision)}</span>
          </td>
        )}
        <td style={{ width: colWidths.tax, padding: "4px 8px", textAlign: "center", verticalAlign: "middle" }}>
          <span style={{ fontSize: "0.75rem", color: "#555" }}>{childTax}</span>
        </td>
        <td style={{ width: colWidths.price, padding: "4px 8px", textAlign: "center", verticalAlign: "middle" }}>
          <span style={{ fontSize: "0.75rem", color: "#555" }}>{parseFloat(child.realPrice || child.sunitprice || 0).toFixed(pricePrecision)}</span>
        </td>
        <td style={{ width: colWidths.total, padding: "4px 8px", textAlign: "center", verticalAlign: "middle" }}>
          <span style={{ fontSize: "0.75rem", color: "#555" }}>
            {symbolRight ? currSymbol : ""}&nbsp;{parseFloat(child.nettotal || 0).toFixed(pricePrecision)}&nbsp;{symbolLeft ? currSymbol : ""}
          </span>
        </td>
        <td style={{ width: colWidths.actions, padding: "4px 8px" }}></td>
      </tr>
    );
  };

  const renderRow = (record, cartType = "RETURN") => {
    const isSelected = selectedProductInCart.lineId === record.lineId;
    const currSymbol = currenciesList[0]?.currSymbol || "";
    const symbolRight = currenciesList[0]?.symbolRightSide === "Y";
    const symbolLeft = currenciesList[0]?.symbolRightSide === "N";
    const taxStr = record.taxAmount?.toFixed(pricePrecision).length < 8 ? parseFloat(record.taxAmount)?.toFixed(pricePrecision) : `${record.taxAmount?.toString().substring(0, 8)}`;
    const hasChildren = record.children && record.children.length > 0;
    const isExpanded = expandedGroups[record.lineId];

    return (
      <React.Fragment key={record.lineId}>
        <tr className={isSelected ? "tableRowSelectedBlue" : "tableRowNonSelectedWhite"} onClick={() => onRowClick(record)} style={{ cursor: "pointer" }}>
          <td style={{ width: colWidths.name, padding: "8px", verticalAlign: "top" }}>{renderItemName(record)}</td>
          <td style={{ width: colWidths.qty, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>{renderQty(record)}</td>
          {!isReturnExchange && (
            <td style={{ width: colWidths.discount, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>
              <span className="tableRow">{parseFloat(record.discount).toFixed(pricePrecision)}</span>
            </td>
          )}
          <td style={{ width: colWidths.tax, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>
            <span className="tableRow">{taxStr}</span>
          </td>
          <td style={{ width: colWidths.price, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>
            <span className="tableRow">{parseFloat(record.realPrice).toFixed(pricePrecision)}</span>
          </td>
          <td style={{ width: colWidths.total, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>
            <div style={{ position: "relative" }}>
              <span className="tableRow" style={{ width: "12%", left: "0" }}>
                {symbolRight ? currSymbol : ""}&nbsp;{parseFloat(record.nettotal).toFixed(pricePrecision)}&nbsp;{symbolLeft ? currSymbol : ""}
              </span>
            </div>
          </td>
          <td style={{ width: colWidths.actions, padding: "8px", textAlign: "center", verticalAlign: "middle" }}>{renderActions(record, cartType)}</td>
        </tr>
        {hasChildren && isExpanded && record.children.map((child) => renderChildRow(child))}
      </React.Fragment>
    );
  };

  const renderTableHeader = () => (
    <thead>
      <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
        <th style={{ width: colWidths.name, padding: "8px 8px", textAlign: "left", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Item Name</th>
        <th style={{ width: colWidths.qty, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Qty</th>
        {!isReturnExchange && <th style={{ width: colWidths.discount, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Discount</th>}
        <th style={{ width: colWidths.tax, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Tax</th>
        <th style={{ width: colWidths.price, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Price</th>
        <th style={{ width: colWidths.total, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}>Total</th>
        <th style={{ width: colWidths.actions, padding: "8px", textAlign: "center", fontWeight: 500, fontSize: "0.85em", color: "#666" }}></th>
      </tr>
    </thead>
  );

  const renderCartTable = (items, className) => (
    <div
      className={className}
      id="sm-cart-table"
      data-zone="cart"
      data-zone-label="Cart Table"
      tabIndex={-1}
      style={{
        width: "100%",
        overflowY: "auto",
        border: "none",
        borderRadius: "7px",
        padding: isReturnExchange ? "0px" : "0px 7px",
        height: "50.5vh",
        background: themeJSON.productTableBGColor || "#fff",
        fontSize: "0.78rem",
        overflowX: isReturnExchange ? "auto" : "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: isReturnExchange ? "fixed" : "auto" }}>
        {renderTableHeader()}
        <tbody>{items.map((record) => renderRow(record, className === "sm-cart-table-exchange-pos" ? "EXCHANGE" : "RETURN"))}</tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: "1.5%", display: "flex", gap: "1rem", width: "100%" }}>
      <div style={{ width: isReturnExchange ? "50%" : "100%" }}>
        {cart.layAway === "Y" && posConfig?.advanceLayaway === "Y" ? (
          <div style={{ position: "absolute", bottom: "29vh", left: "56vw", display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ opacity: "0.5" }}>ADVANCE</span>
          </div>
        ) : null}
        {watermark()}
        {isReturnExchange ? (
          <div
            style={{
              width: "100%",
              height: "6vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#FDEAEA",
              padding: "0 1rem",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              border: "1px solid #f5c2c7",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ backgroundColor: "#E53935", color: "#fff", fontWeight: 600, fontSize: "0.6em", padding: "2px 6px", borderRadius: "4px" }}>RETURN</span>
              <span style={{ fontSize: "0.6em", fontWeight: 600, color: "#2F3856" }}>Returned Items</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Button size="small" icon={<UserOutlined />} style={{ borderRadius: "6px", fontWeight: 500, fontSize: "0.8em" }} disabled={true}>
                Change Customer
              </Button>
              <div style={{ display: "flex", alignItems: "center" }}>
                {renderReturnAmount()}

                {posConfig?.enableOrderLevelNarration === "Y" && (
                  <img
                    src={NotesImage}
                    onClick={() => {
                      setActiveCartTable("RETURN");
                      setNotesPopup(true);

                      if (cart.description && cart.description.trim() !== "") {
                        setNotesValue(cart.description);
                      }

                      setIsInputFocused(true);
                    }}
                    style={{ height: "0.85rem", paddingLeft: "0.3rem", cursor: "pointer", paddingRight: "0.3rem" }}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              height: "6vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              textAlign: "center",
              backgroundColor: "#7A92AC",
              padding: "1vw 0.5rem",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
              color: "#fff",
            }}
          >
            {renderInvoice()}
            <span style={{ fontSize: "0.7rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {renderDocumentNo()}
              {posConfig?.enableOrderLevelNarration === "Y" && (
                <img
                  src={NotesAdd}
                  onClick={() => {
                    setActiveCartTable("RETURN");
                    setNotesPopup(true);
                    if (cart.description && cart.description.trim() !== "") setNotesValue(cart.description);
                    setIsInputFocused(true);
                  }}
                  style={{ height: "0.85rem", paddingLeft: "0.7rem", cursor: "pointer", paddingRight: "0.3rem" }}
                />
              )}
            </span>
          </div>
        )}
        {renderCartTable(cartItems, isReturnExchange ? "sm-cart-table-return-pos" : "sm-cart-table-pos")}
      </div>
      {isReturnExchange ? (
        <div style={{ width: "50%" }}>
          <div
            style={{
              width: "100%",
              height: "6vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#E6F4EA",
              padding: "0 1rem",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              border: "1px solid #A5D6A7",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ backgroundColor: "#2F8F46", color: "#fff", fontWeight: 600, fontSize: "0.5em", padding: "2px 6px", borderRadius: "4px" }}>NEW ITEM</span>
              <span style={{ fontSize: "0.6em", fontWeight: 600, color: "#2F3856" }}>Exchange Items</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Button
                size="small"
                icon={<UserOutlined />}
                style={{ borderRadius: "6px", fontWeight: 500, fontSize: "0.7em", padding: "0 4px", height: "22px", lineHeight: "22px" }}
                onClick={() => {
                  setDisplayCustomerSearch(true);
                  setCloseCustomerFlag(true);
                  setProductSearchInput("");
                }}
              >
                {tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === exchangeCart?.customer?.cwrCustomerId
                  ? "Change Customer"
                  : exchangeCart?.customer?.name || "Change Customer"}
              </Button>
              <div style={{ display: "flex", alignItems: "center" }}>
                {renderExchangeAmount()}

                {posConfig?.enableOrderLevelNarration === "Y" && (
                  <img
                    src={NotesImage}
                    onClick={() => {
                      setActiveCartTable("EXCHANGE");
                      setNotesPopup(true);

                      if (exchangeCart?.description && exchangeCart.description.trim() !== "") {
                        setExchangeNotesValue(exchangeCart.description);
                      } else {
                        setExchangeNotesValue("");
                      }

                      setIsInputFocused(true);
                    }}
                    style={{ height: "0.85rem", paddingLeft: "0.3rem", cursor: "pointer", paddingRight: "0.3rem" }}
                  />
                )}
              </div>
            </div>
          </div>
          {renderCartTable(exchangeItems, "sm-cart-table-exchange-pos")}
        </div>
      ) : null}
    </div>
  );
};

export default SMCartTable;
